(() => {
  const page = document.querySelector('[data-nbl-page]');
  if (!page) return;

  const groupNames = ['series', 'method', 'tier', 'style', 'material', 'color', 'route', 'feature'];
  const groupLabels = {
    series: 'Series',
    method: 'Customization Method',
    tier: 'Product Tier',
    style: 'Sock Style',
    material: 'Material',
    color: 'Base Color',
    route: 'Production Route',
    feature: 'Features'
  };
  const pageSize = 9;
  const grid = page.querySelector('[data-nbl-grid]');
  const cards = Array.from(page.querySelectorAll('[data-nbl-card]'));
  const options = Array.from(page.querySelectorAll('[data-nbl-option]'));
  const search = page.querySelector('[data-nbl-search]');
  const sort = page.querySelector('[data-nbl-sort]');
  const count = page.querySelector('[data-nbl-count]');
  const active = page.querySelector('[data-nbl-active]');
  const pagination = page.querySelector('[data-nbl-pagination]');
  const empty = page.querySelector('[data-nbl-empty]');
  const filterPanel = page.querySelector('[data-nbl-filters]');
  const filterOpen = page.querySelector('[data-nbl-filter-open]');
  const filterClose = document.querySelectorAll('[data-nbl-filter-close]');
  const filterOverlay = document.querySelector('.nbl-filter-overlay');
  const filterBadge = page.querySelector('[data-nbl-filter-badge]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!grid || !search || !sort || !count || !active || !pagination || !empty) return;

  const state = {
    query: '',
    sort: 'popular',
    page: 1,
    selected: Object.fromEntries(groupNames.map((group) => [group, new Set()]))
  };

  const allowedValues = Object.fromEntries(
    groupNames.map((group) => [
      group,
      new Set(options.filter((input) => input.dataset.group === group).map((input) => input.value))
    ])
  );

  const valuesFor = (card, group) => (card.dataset[group] || '').split(/\s+/).filter(Boolean);

  const matches = (card, ignoredGroup = '') => {
    if (state.query && !(card.dataset.search || '').includes(state.query)) return false;

    return groupNames.every((group) => {
      if (group === ignoredGroup || state.selected[group].size === 0) return true;
      const cardValues = valuesFor(card, group);
      return Array.from(state.selected[group]).some((value) => cardValues.includes(value));
    });
  };

  const optionLabel = (input) => input.closest('.nbl-option')?.querySelector('.nbl-option__name')?.textContent?.trim() || input.value;

  const readUrl = () => {
    const params = new URL(window.location.href).searchParams;

    groupNames.forEach((group) => {
      state.selected[group].clear();
      const values = (params.get(group) || '').split(',').filter(Boolean);
      values.forEach((value) => {
        if (allowedValues[group].has(value)) state.selected[group].add(value);
      });
    });

    state.query = (params.get('q') || '').trim().toLowerCase();
    state.sort = ['popular', 'title-asc', 'title-desc', 'price-asc', 'price-desc'].includes(params.get('sort'))
      ? params.get('sort')
      : 'popular';
    state.page = Math.max(1, Number.parseInt(params.get('page') || '1', 10) || 1);
  };

  const syncControls = () => {
    options.forEach((input) => {
      input.checked = state.selected[input.dataset.group]?.has(input.value) || false;
    });
    search.value = state.query;
    sort.value = state.sort;
  };

  const writeUrl = () => {
    const url = new URL(window.location.href);

    groupNames.forEach((group) => {
      const selected = Array.from(state.selected[group]);
      if (selected.length) url.searchParams.set(group, selected.join(','));
      else url.searchParams.delete(group);
    });

    if (state.query) url.searchParams.set('q', state.query);
    else url.searchParams.delete('q');

    if (state.sort !== 'popular') url.searchParams.set('sort', state.sort);
    else url.searchParams.delete('sort');

    if (state.page > 1) url.searchParams.set('page', String(state.page));
    else url.searchParams.delete('page');

    window.history.replaceState({}, '', url);
  };

  const updateOptionCounts = () => {
    options.forEach((input) => {
      const group = input.dataset.group;
      const optionCount = cards.reduce((total, card) => {
        if (!matches(card, group)) return total;
        return total + (valuesFor(card, group).includes(input.value) ? 1 : 0);
      }, 0);
      const label = input.closest('.nbl-option');
      const countElement = label?.querySelector('[data-nbl-option-count]');
      const disabled = optionCount === 0 && !input.checked;

      if (countElement) countElement.textContent = String(optionCount);
      input.disabled = disabled;
      label?.classList.toggle('is-disabled', disabled);
    });
  };

  const updateActiveFilters = () => {
    active.replaceChildren();
    let activeCount = 0;

    if (state.query) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'nbl-chip';
      chip.dataset.group = 'query';
      chip.textContent = `Search: ${state.query}`;
      active.append(chip);
      activeCount += 1;
    }

    groupNames.forEach((group) => {
      state.selected[group].forEach((value) => {
        const input = options.find((option) => option.dataset.group === group && option.value === value);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'nbl-chip';
        chip.dataset.group = group;
        chip.dataset.value = value;
        chip.textContent = `${groupLabels[group]}: ${input ? optionLabel(input) : value}`;
        active.append(chip);
        activeCount += 1;
      });
    });

    active.hidden = activeCount === 0;
    if (filterBadge) {
      filterBadge.textContent = String(activeCount);
      filterBadge.hidden = activeCount === 0;
    }
  };

  const sortCards = (matchingCards) => {
    const sorted = [...matchingCards];
    sorted.sort((a, b) => {
      if (state.sort === 'title-asc') return (a.dataset.title || '').localeCompare(b.dataset.title || '');
      if (state.sort === 'title-desc') return (b.dataset.title || '').localeCompare(a.dataset.title || '');
      if (state.sort === 'price-asc') return Number(a.dataset.price || 0) - Number(b.dataset.price || 0);
      if (state.sort === 'price-desc') return Number(b.dataset.price || 0) - Number(a.dataset.price || 0);
      return Number(a.dataset.order || 0) - Number(b.dataset.order || 0);
    });
    return sorted;
  };

  const buildPagination = (totalPages, shouldScroll) => {
    pagination.replaceChildren();
    if (totalPages <= 1) return;

    const createButton = (label, pageNumber, ariaLabel, current = false) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.setAttribute('aria-label', ariaLabel);
      if (current) button.setAttribute('aria-current', 'page');
      button.addEventListener('click', () => {
        state.page = pageNumber;
        render({ updateUrl: true, shouldScroll: true });
      });
      return button;
    };

    const previous = createButton('‹', Math.max(1, state.page - 1), 'Previous page');
    previous.disabled = state.page === 1;
    pagination.append(previous);

    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      pagination.append(createButton(String(pageNumber), pageNumber, `Page ${pageNumber}`, pageNumber === state.page));
    }

    const status = document.createElement('span');
    status.className = 'nbl-pagination__status';
    status.textContent = `of ${totalPages}`;
    pagination.append(status);

    const next = createButton('›', Math.min(totalPages, state.page + 1), 'Next page');
    next.disabled = state.page === totalPages;
    pagination.append(next);

    if (shouldScroll) {
      const header = page.querySelector('[data-nbh-header]');
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const top = page.querySelector('.nbl-results')?.getBoundingClientRect().top || 0;
      window.scrollTo({
        top: top + window.scrollY - headerHeight - 16,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }
  };

  function render({ updateUrl = true, shouldScroll = false } = {}) {
    const matchingCards = sortCards(cards.filter((card) => matches(card)));
    const totalPages = Math.max(1, Math.ceil(matchingCards.length / pageSize));
    state.page = Math.min(Math.max(1, state.page), totalPages);
    const start = (state.page - 1) * pageSize;
    const visibleCards = matchingCards.slice(start, start + pageSize);

    cards.forEach((card) => { card.hidden = true; });
    visibleCards.forEach((card) => {
      grid.append(card);
      card.hidden = false;
    });

    const total = document.createElement('strong');
    total.textContent = String(matchingCards.length);
    count.replaceChildren(total, document.createTextNode(` ${matchingCards.length === 1 ? 'product' : 'products'} found`));
    empty.hidden = matchingCards.length !== 0;

    updateOptionCounts();
    updateActiveFilters();
    buildPagination(matchingCards.length ? totalPages : 0, shouldScroll);
    if (updateUrl) writeUrl();
    page.classList.add('is-ready');
  }

  const updateStateFromControls = () => {
    groupNames.forEach((group) => state.selected[group].clear());
    options.forEach((input) => {
      if (input.checked) state.selected[input.dataset.group].add(input.value);
    });
    state.query = search.value.trim().toLowerCase();
    state.sort = sort.value;
    state.page = 1;
  };

  options.forEach((input) => {
    input.addEventListener('change', () => {
      updateStateFromControls();
      render();
    });
  });

  let searchTimer;
  search.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      updateStateFromControls();
      render();
    }, 120);
  });

  sort.addEventListener('change', () => {
    updateStateFromControls();
    render();
  });

  active.addEventListener('click', (event) => {
    const chip = event.target.closest('.nbl-chip');
    if (!chip) return;

    if (chip.dataset.group === 'query') {
      state.query = '';
      search.value = '';
    } else if (chip.dataset.group && chip.dataset.value) {
      state.selected[chip.dataset.group]?.delete(chip.dataset.value);
      const input = options.find((option) => option.dataset.group === chip.dataset.group && option.value === chip.dataset.value);
      if (input) input.checked = false;
    }

    state.page = 1;
    render();
  });

  page.querySelectorAll('[data-nbl-clear]').forEach((button) => {
    button.addEventListener('click', () => {
      groupNames.forEach((group) => state.selected[group].clear());
      state.query = '';
      state.page = 1;
      syncControls();
      render();
    });
  });

  const setFilterOpen = (open) => {
    if (!filterPanel || !filterOpen || !filterOverlay) return;
    filterPanel.classList.toggle('is-open', open);
    filterOpen.setAttribute('aria-expanded', String(open));
    filterOverlay.hidden = !open;
    document.body.classList.toggle('nbl-filter-open', open);
    if (open) filterPanel.querySelector('input, button, summary')?.focus();
    else filterOpen.focus();
  };

  filterOpen?.addEventListener('click', () => setFilterOpen(true));
  filterClose.forEach((button) => button.addEventListener('click', () => setFilterOpen(false)));
  document.addEventListener('keydown', (event) => {
    if (!filterPanel?.classList.contains('is-open')) return;
    if (event.key === 'Escape') setFilterOpen(false);
    if (event.key === 'Tab') {
      const controls = Array.from(filterPanel.querySelectorAll('button, input, summary, a[href]'))
        .filter((element) => !element.disabled && element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  });

  window.matchMedia('(max-width: 989px)').addEventListener('change', (event) => {
    if (!event.matches && filterPanel?.classList.contains('is-open')) setFilterOpen(false);
  });

  window.addEventListener('popstate', () => {
    readUrl();
    syncControls();
    render({ updateUrl: false });
  });

  readUrl();
  syncControls();
  render({ updateUrl: false });
})();


